/**
 * Budget Consolidation — org-tree budget rollup (PlanBudgetOrgRollup spec).
 *
 * A manager/controller view (gated on Finance.PlanEntry.ViewRollup): pick a Plan Version and check one
 * or more org nodes in the left tree; the page consolidates the budget of everything entered under the
 * union of those nodes' subtrees — the submitted GrandTotal per fiscal period, the separate
 * NotYetSubmittedAmount, a per-node / per-child breakdown with submission-status badges, and a
 * dimensional pivot (Header Type → the Plan View's row dimensions, with Volym/Pris/Värde) offered per
 * Plan View as tabs. Selection, the open tab and the statement tab's chosen template are encoded in
 * the URL (shareable).
 *
 * Hand-written (like PlanEntryGrid), calling the PlanBudgetRollupAppService endpoints directly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EntityDecisionBanner } from '@/components/EntityDecisionBanner';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeItem, type TreeItemProps } from '@mui/x-tree-view/TreeItem';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { useOrgUnits } from '@/hooks/core/useOrgUnit';
import { usePlanVersions } from '@/hooks/finance/usePlanVersion';
import { useFiscalPeriods } from '@/hooks/finance/useFiscalPeriod';
import { apiClient } from '@/lib/apiClient';
import { formatNumber } from '@/lib/locale';
import { compareTreeSiblings } from '@/lib/treeLookup';
import type { OrgUnit } from '@/types/core/OrgUnit';
import { PlanRollupPivot } from './PlanRollupPivot';
import { PlanRollupByAccount, BY_ACCOUNT_DEFAULT_GROUP_BY } from './PlanRollupByAccount';
import { PlanRollupStatement } from './PlanRollupStatement';
import { authService } from '@/services/auth/authService';
import {
  applyOrgScope,
  buildOrgTreeIndex,
  collectSubtree,
  expandSelection,
  parseInitialSelection,
  scopeToLegalEntity,
  toSelectionParams,
  withContextChecked,
  type OrgScope,
} from './planRollupSelection';
import { RollupShapingToolbar } from './RollupShapingToolbar';
import { RollupUnfilteredTotalRow, useFilteredTotalLabel } from './RollupFilteredTotal';
import {
  parseShaping,
  shapingQueryKey,
  shapingRequestFields,
  writeShaping,
  type RollupShaping,
} from './planRollupShaping';
import { SummaryTile } from '@/components/SummaryTile';

// ── Response shape (custom service, no generated proxy; enums arrive as string names) ────────────────

interface RollupPeriodAmount { fiscalPeriodId: string; amount: number; signedAmount: number; quantity?: number | null }
interface RollupGroupKey {
  childOrgUnitId?: string | null;
  childOrgUnitName?: string | null;
  isDirect: boolean;
  submissionStatuses: string[];
}
interface RollupGroup { key: RollupGroupKey; periods: RollupPeriodAmount[]; groupTotal: number; signedTotal: number }
interface PlanViewTab { id: string; name: string }
interface PlanRollupDto {
  periods: RollupPeriodAmount[];
  grandTotal: number;
  notYetSubmittedAmount: number;
  /** The KPI tiles' figures, deliberately left unshaped — the anchor a narrowed view is read against (§11). */
  unfilteredGrandTotal: number;
  unfilteredNotYetSubmittedAmount: number;
  /** The consolidation as a result: each amount signed by its account's reporting sign. */
  signedResult: number;
  unfilteredSignedResult: number;
  /** How much of the gross is deliberately outside the result (memo / balance-sheet / internal). */
  excludedAmount: number;
  /** At least one contributing account's sign was guessed from its type — uncertain, not incomplete. */
  signedResultIsApproximate: boolean;
  excludedAccountsAdvisory?: string | null;
  /** Whether a filter or period range actually narrowed this read — the server's answer, not the request's. */
  isShaped: boolean;
  breakdown?: RollupGroup[] | null;
  availablePlanViews: PlanViewTab[];
  warnings: string[];
}

// POST like every other rollup read: the filter AST has no upper bound and does not belong in a request
// line (§6). Sharing is unaffected — the shareable artifact is the SPA route, a separate URL.
const ROLLUP_ENDPOINT = '/api/finance/planbudgetrollup/rollup';
const ORG_SCOPE_ENDPOINT = '/api/finance/planbudgetrollup/org-scope';
const money = (n: number | null | undefined) => formatNumber(n ?? 0, { maximumFractionDigits: 2 });

function statusChipColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'SUBMITTED': return 'success';
    case 'DRAFT': return 'warning';
    case 'RETURNED': return 'error';
    default: return 'default';
  }
}

interface TreeNode { id: string; label: string; children: TreeNode[] }

function buildTree(orgUnits: OrgUnit[]): TreeNode[] {
  // The source list is sorted up front with the shared sibling rule, so every group comes out ordered.
  const ordered = [...orgUnits].sort(compareTreeSiblings);
  const map = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];
  for (const o of ordered) map.set(o.id, { id: o.id, label: o.name, children: [] });
  for (const o of ordered) {
    const node = map.get(o.id)!;
    if (o.parentId && map.has(o.parentId)) map.get(o.parentId)!.children.push(node);
    else roots.push(node);
  }
  return roots;
}

export function PlanRollupView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ planVersionId?: string; orgUnitId?: string }>();
  // Read once at mount: this is the initial state, not a live view of the address bar.
  const initialQuery = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialSelection = useMemo(
    () => parseInitialSelection(params.orgUnitId, initialQuery),
    [params.orgUnitId, initialQuery],
  );

  const [versionId, setVersionId] = useState<string>(params.planVersionId ?? initialQuery.get('version') ?? '');
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>(initialSelection.selected);
  // A `roots=`/`exclude=` URL cannot be expanded until the org tree has loaded. Until then the URL must
  // not be rewritten, or the effect below would clear the very selection we are about to restore.
  const [selectionHydrated, setSelectionHydrated] = useState(false);
  // The incoming link named org nodes outside this user's scope — shown once, never silently applied.
  const [linkOutOfScope, setLinkOutOfScope] = useState(false);
  // One entry per drill-down click, so re-rooting the rollup is reversible. Ephemeral by design:
  // the URL carries where you are, not how you got there, so a reload starts a fresh trail.
  const [drillTrail, setDrillTrail] = useState<string[][]>([]);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(initialQuery.get('tab') ?? 'period');
  // The statement tab's chosen template. Empty means "whatever this legal entity's default is", which
  // the server resolves — so a link without it still opens on a sensible statement.
  const [statementTemplateId, setStatementTemplateId] = useState<string>(initialQuery.get('template') ?? '');
  // The By Account tab's grouping chain. Absent from the URL means the preset, so an old link keeps
  // rendering the two levels that tab always had.
  const [groupBy, setGroupBy] = useState<string[]>(() => {
    const raw = initialQuery.get('groupBy');
    return raw ? raw.split(',').filter(Boolean) : BY_ACCOUNT_DEFAULT_GROUP_BY;
  });
  // The filter and the period window — page-level, because they apply identically to every tab and
  // applying them per tab is what would make the tabs silently incomparable (§9.1).
  const [shaping, setShaping] = useState<RollupShaping>(() => parseShaping(initialQuery));

  const { data: versions } = usePlanVersions();
  const { data: orgUnits } = useOrgUnits();
  const { data: fiscalPeriods } = useFiscalPeriods();

  // Read fresh on every render: the current legal entity lives in localStorage with no reactive
  // wrapper (as in useEnhancedJsonForms), so a value captured in a memo would survive a switch.
  const currentLegalEntityId = authService.getLegalEntityId();

  // Which part of the tree this user may consolidate. The server gate is the boundary; this only
  // shapes the picker so it cannot offer a node the request would be denied on. Never cached across
  // users: the query key carries no user id, but the client is torn down on logout.
  const { data: orgScope } = useQuery<OrgScope, Error>({
    queryKey: ['planBudgetRollupOrgScope'],
    queryFn: async () => (await apiClient.get<OrgScope>(ORG_SCOPE_ENDPOINT)).data,
    staleTime: 60_000,
  });

  // Single-LE selection — see scopeToLegalEntity. The service still spans legal entities; this only
  // narrows what the tree offers.
  const legalEntityUnits = useMemo(
    () => scopeToLegalEntity(orgUnits ?? [], currentLegalEntityId),
    [orgUnits, currentLegalEntityId],
  );

  // …then narrowed again by org entitlement: the caller's membership subtrees, with their ancestors
  // kept as non-selectable context so the reader can see where they sit.
  const { nodes: scopedOrgUnits, contextIds } = useMemo(
    () => (orgScope
      ? applyOrgScope(legalEntityUnits, orgScope)
      : { nodes: [] as typeof legalEntityUnits, contextIds: new Set<string>() }),
    [legalEntityUnits, orgScope],
  );

  const treeItems = useMemo(() => buildTree(scopedOrgUnits), [scopedOrgUnits]);
  const orgIndex = useMemo(() => buildOrgTreeIndex(scopedOrgUnits), [scopedOrgUnits]);
  const selectableIds = useMemo(
    () => new Set(orgIndex.allNodeIds.filter((id) => !contextIds.has(id))),
    [orgIndex, contextIds],
  );
  // The tree before the entitlement narrowing — used only to tell "the link asked for a node you may
  // not consolidate" apart from "the link asked for a node that does not exist".
  const unscopedIndex = useMemo(() => buildOrgTreeIndex(legalEntityUnits), [legalEntityUnits]);

  // Selection cascades to descendants only (parents:false) — exactly the permission tree: checking a
  // node checks its whole subtree, and unchecking one branch leaves the ancestor checked. From that
  // checked set we derive what the rollup needs: the top-most checked nodes are the subtree roots, and
  // any node whose parent is checked but itself is not is the top of an unchecked branch to prune. The
  // API then sums a partially-selected parent's own budget while leaving the pruned branch out — which
  // no single subtree root can express, since a root always drags in its full subtree. The same pair is
  // what the URL carries (planRollupSelection.ts).
  // Everything downstream reads the selection through this filter, so a checked id outside the
  // caller's scope can never reach the request. The server would deny it anyway (that is the actual
  // boundary); this keeps the page from firing a request it knows will 403.
  const effectiveSelectedIds = useMemo(
    () => selectedNodeIds.filter((id) => selectableIds.has(id)),
    [selectedNodeIds, selectableIds],
  );

  const { roots: topMostCheckedIds, exclude: excludedIds } = useMemo(
    () => toSelectionParams(effectiveSelectedIds, orgIndex),
    [effectiveSelectedIds, orgIndex],
  );

  // What the tree renders as checked. A context ancestor is never stored in the selection — the
  // server would refuse to root a consolidation there — but it shows checked once everything
  // selectable beneath it is, so ticking it reads as "select all of my part below here". Derived
  // rather than stored, so the state survives a reload of the roots/exclude URL unchanged.
  const treeSelectedItems = useMemo(
    () => withContextChecked(effectiveSelectedIds, contextIds, selectableIds, orgIndex),
    [effectiveSelectedIds, contextIds, orgIndex, selectableIds],
  );

  // Context ancestors stay visually apart from the caller's own nodes: checking one is a shortcut
  // for its selectable descendants, and the resulting total is the part of that branch the caller
  // may see — not the branch's budget. Memoized because a fresh component identity per render
  // would remount the whole tree.
  const ScopedTreeItem = useMemo(
    () => function ScopedTreeItem(props: TreeItemProps) {
      const isContext = contextIds.has(props.itemId);
      return (
        <TreeItem
          {...props}
          sx={isContext
            ? { '& > .MuiTreeItem-content .MuiTreeItem-label': { fontStyle: 'italic', opacity: 0.65 } }
            : undefined}
        />
      );
    },
    [contextIds],
  );

  // Restore the incoming link once the org tree AND the caller's scope are known, and reconcile it
  // against that scope. Expansion runs over the UNSCOPED index so a link pointing outside the scope is
  // recognised as such rather than silently expanding to nothing; what survives the filter is then
  // what gets checked. A shared link from someone with a wider scope is the ordinary case here, so it
  // gets an explanation and the caller's own part of the tree — not a dead end.
  useEffect(() => {
    if (selectionHydrated || !orgScope) return;
    // Only a link that actually names nodes has to wait for the tree; without one there is nothing to
    // reconcile, and blocking would leave the URL permanently unsynced when no legal entity is picked.
    const carriesSelection = initialSelection.pending !== null || initialSelection.selected.length > 0;
    if (carriesSelection && unscopedIndex.allNodeIds.length === 0) return;

    const requested = initialSelection.pending
      ? expandSelection(initialSelection.pending.roots, initialSelection.pending.exclude, unscopedIndex)
      : initialSelection.selected;
    const allowed = requested.filter((id) => selectableIds.has(id));

    if (allowed.length < requested.length) {
      setLinkOutOfScope(true);
      // Nothing of the link survived: fall back to the caller's own membership subtrees rather than an
      // empty page, since that is the consolidation they came here to see.
      setSelectedNodeIds(allowed.length > 0
        ? allowed
        : orgScope.rootOrgUnitIds.filter((id) => selectableIds.has(id)));
    } else if (initialSelection.pending) {
      setSelectedNodeIds(allowed);
    }
    setSelectionHydrated(true);
  }, [selectionHydrated, initialSelection, orgScope, unscopedIndex, selectableIds]);

  // Keep the URL in sync so a selection is shareable/bookmarkable. Encoded as the subtree roots plus the
  // pruned branch tops rather than the expanded checked set: "the whole group" is then one id instead of
  // one per node, which is what keeps a bookmarked link under the ~8 KB request line every static host
  // enforces on reload.
  useEffect(() => {
    if (!selectionHydrated) return;
    const qs = new URLSearchParams();
    if (versionId) qs.set('version', versionId);
    if (topMostCheckedIds.length) qs.set('roots', topMostCheckedIds.join(','));
    if (excludedIds.length) qs.set('exclude', excludedIds.join(','));
    // The requested tab, not the resolved one: a Plan View tab only exists once the rollup has told us
    // which Plan Views have budget under the selection, so writing the resolved value here would
    // overwrite a `?tab=pv:<id>` deep link with "period" in the moment before it becomes valid.
    if (activeTab && activeTab !== 'period') qs.set('tab', activeTab);
    if (statementTemplateId) qs.set('template', statementTemplateId);
    // Only when it differs from the preset — the common link stays short, and §12.1's budget is spent
    // on the selection rather than on restating a default.
    const isDefaultChain =
      groupBy.length === BY_ACCOUNT_DEFAULT_GROUP_BY.length
      && groupBy.every((token, i) => token === BY_ACCOUNT_DEFAULT_GROUP_BY[i]);
    if (!isDefaultChain) qs.set('groupBy', groupBy.join(','));
    writeShaping(qs, shaping);
    const target = `/finance/plan-rollup${qs.toString() ? `?${qs.toString()}` : ''}`;
    if (window.location.pathname + window.location.search !== target) {
      navigate(target, { replace: true });
    }
  }, [versionId, topMostCheckedIds, excludedIds, selectionHydrated, activeTab, statementTemplateId, groupBy, shaping, navigate]);

  const inclusion = includeDrafts ? 'ALL' : 'SUBMITTED';

  const { data: rollup, isFetching, isError, error } = useQuery<PlanRollupDto, Error>({
    queryKey: [
      'planBudgetRollup', versionId, [...topMostCheckedIds].sort(), [...excludedIds].sort(), inclusion,
      shapingQueryKey(shaping),
    ],
    queryFn: async () => {
      return (await apiClient.post<PlanRollupDto>(ROLLUP_ENDPOINT, {
        planVersionId: versionId,
        orgUnitIds: topMostCheckedIds,
        excludeOrgUnitIds: excludedIds,
        breakdown: 'CHILD',
        inclusion,
        ...shapingRequestFields(shaping),
      })).data;
    },
    enabled: !!versionId && topMostCheckedIds.length > 0 && !!orgScope,
  });

  // Re-reads the numbers on screen — the headline cards and the open tab — without changing the
  // selection. Invalidation marks the other tabs' and filters' cached results stale too, so they
  // re-read when next shown instead of serving what they held before the refresh.
  const queryClient = useQueryClient();
  const handleRefresh = useCallback(() => {
    for (const key of ['planBudgetRollup', 'planRollupPivot', 'planRollupGrouped', 'planRollupStatement']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [queryClient]);

  const filteredLabel = useFilteredTotalLabel();
  const isShaped = rollup?.isShaped ?? false;

  const versionGroups = useMemo(() => {
    const byScenario = new Map<string, typeof versions>();
    (versions ?? []).forEach((v) => {
      const key = v.planScenarioName ?? '—';
      const bucket = byScenario.get(key) ?? [];
      bucket.push(v);
      byScenario.set(key, bucket);
    });
    return [...byScenario.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [versions]);

  const periodMeta = useMemo(
    () => new Map((fiscalPeriods ?? []).map((p) => [p.id, { name: p.name, order: p.periodNumber }])),
    [fiscalPeriods],
  );
  const orderedPeriods = useMemo(() => {
    const periods = [...(rollup?.periods ?? [])];
    periods.sort((a, b) => (periodMeta.get(a.fiscalPeriodId)?.order ?? 0) - (periodMeta.get(b.fiscalPeriodId)?.order ?? 0));
    return periods;
  }, [rollup, periodMeta]);

  // Drilling selects the child's whole subtree so the tree checkboxes match the cascade behavior,
  // pushing the selection it replaces so the step can be undone.
  const drillTo = useCallback(
    (childId: string) => {
      setDrillTrail((trail) => [...trail, effectiveSelectedIds]);
      setSelectedNodeIds(collectSubtree(childId, orgIndex));
    },
    [orgIndex, effectiveSelectedIds],
  );

  const drillBack = useCallback(() => {
    if (drillTrail.length === 0) return;
    // Both setters called from the handler, never from inside an updater: an updater must stay pure
    // or StrictMode's double invocation applies the side effect twice.
    setSelectedNodeIds(drillTrail[drillTrail.length - 1]);
    setDrillTrail((trail) => trail.slice(0, -1));
  }, [drillTrail]);

  // Where a step back lands, for the button's label — the name only when the previous view was a
  // single subtree, since "back to 3 nodes" tells the reader nothing.
  const drillBackLabel = useMemo(() => {
    if (drillTrail.length === 0) return null;
    const previous = drillTrail[drillTrail.length - 1];
    const roots = toSelectionParams(previous, orgIndex).roots;
    if (roots.length !== 1) return null;
    return scopedOrgUnits.find((o) => o.id === roots[0])?.name ?? null;
  }, [drillTrail, orgIndex, scopedOrgUnits]);

  const hasSelection = effectiveSelectedIds.length > 0;

  // Right-panel tabs: period/node, one per Plan View under the selection, then the two fixed
  // account-based views. The statement is its own tab rather than a mode of By Account: it selects
  // rather than partitions, so it does not inherit that view's "rows sum to the total" invariant.
  const planViewTabs = rollup?.availablePlanViews ?? [];
  const tabValues = ['period', ...planViewTabs.map((v) => `pv:${v.id}`), 'account', 'statement'];
  const safeTab = tabValues.includes(activeTab) ? activeTab : 'period';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <ViewToolbar onRefresh={handleRefresh} />
      <Box sx={{ display: 'flex', gap: 2, px: 2, pb: 2, flex: 1, minHeight: 0 }}>
        {/* Left: org tree with checkbox multi-select */}
        <Paper variant="outlined" sx={{ width: 300, minWidth: 260, overflow: 'auto', flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            {t('Finance.PlanRollup.Organization', { defaultValue: 'Organization' })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
            {contextIds.size > 0
              ? t('Finance.PlanRollup.CheckNodesScopedHint', {
                defaultValue: 'Check one or more nodes to consolidate. Nodes shown in italics are above your access — checking one selects everything below it that you can see.',
              })
              : t('Finance.PlanRollup.CheckNodesHint', { defaultValue: 'Check one or more nodes to consolidate' })}
          </Typography>
          {!orgUnits || !orgScope ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          ) : treeItems.length === 0 ? (
            // An empty tree with no explanation reads as "the organization is empty", which is a
            // different and wrong conclusion from either of the two real causes.
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
              {legalEntityUnits.length === 0
                ? t('Finance.PlanRollup.NoLegalEntity', {
                  defaultValue: 'Select a legal entity in the header to choose org nodes.',
                })
                : t('Finance.PlanRollup.NoOrgScope', {
                  defaultValue:
                    'You are not a member of any org unit in this legal entity, so there is nothing to consolidate. Ask an administrator to add you on the Organization page.',
                })}
            </Typography>
          ) : (
            <RichTreeView
              items={treeItems}
              checkboxSelection
              multiSelect
              selectionPropagation={{ descendants: true, parents: false }}
              expansionTrigger="iconContainer"
              slots={{ item: ScopedTreeItem }}
              selectedItems={treeSelectedItems}
              // A context ancestor may be ticked as a shortcut, but it must never reach the request —
              // the server denies rooting a consolidation at a node the caller is not under. Filtering
              // here is the boundary's mirror; descendant propagation cannot slip one through.
              onSelectedItemsChange={(_e, ids) => {
                setDrillTrail([]);
                setSelectedNodeIds((ids as string[]).filter((id) => selectableIds.has(id)));
              }}
              defaultExpandedItems={orgIndex.allNodeIds as string[]}
              sx={{ px: 1, pb: 2 }}
            />
          )}
        </Paper>

        {/* Right: content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Typography variant="h5" gutterBottom>{t('Menu:Finance.PlanRollup', { defaultValue: 'Budget Consolidation' })}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Finance.PlanRollup.Subtitle', {
              defaultValue: 'Consolidated budget of everything entered under the selected org node(s) — live, before publish.',
            })}
          </Typography>

          {/* A link shared by someone with a wider scope. Said out loud rather than quietly narrowed:
              a total that silently covers less than the link asked for is a wrong number. */}
          {linkOutOfScope && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setLinkOutOfScope(false)}>
              {t('Finance.PlanRollup.LinkOutOfScope', {
                defaultValue:
                  'This link points at org nodes outside your access. Showing your part of the organization instead.',
              })}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Select
              size="small"
              displayEmpty
              value={versionId}
              onChange={(e) => setVersionId(e.target.value)}
              sx={{ minWidth: 280 }}
              renderValue={(val) => {
                if (!val) return <em>{t('Finance.PlanRollup.SelectVersion', { defaultValue: 'Select plan version…' })}</em>;
                const v = (versions ?? []).find((x) => x.id === val);
                return v ? `${v.planScenarioName ?? '—'} · ${v.label ?? `v${v.versionNo}`}` : String(val);
              }}
            >
              <MenuItem value="" disabled><em>{t('Finance.PlanRollup.SelectVersion', { defaultValue: 'Select plan version…' })}</em></MenuItem>
              {versionGroups.flatMap(([scenario, vs]) => [
                <ListSubheader key={`h-${scenario}`}>{scenario}</ListSubheader>,
                ...(vs ?? []).map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.label ?? `v${v.versionNo}`} ({t(`Enum:PlanVersionState:${v.status}`, { defaultValue: v.status })})
                  </MenuItem>
                )),
              ])}
            </Select>

            <FormControlLabel
              control={<Switch checked={includeDrafts} onChange={(e) => setIncludeDrafts(e.target.checked)} />}
              label={t('Finance.PlanRollup.IncludeDrafts', { defaultValue: 'Include drafts in total' })}
            />

            <RollupShapingToolbar value={shaping} onChange={setShaping} />

            {isFetching && <CircularProgress size={20} />}
          </Paper>

          {/* The approval decision, where the evidence is. An approver reviewing a version is
              already looking at the numbers here; sending them back to the version page to
              press Approve adds a round trip and nothing else. The banner shows itself only
              when this version has an open decision waiting on the current user. */}
          <EntityDecisionBanner entityType="PlanVersion" entityId={versionId || undefined} />

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error?.message ?? t('Finance.PlanRollup.LoadFailed', { defaultValue: 'Failed to load rollup.' })}
            </Alert>
          )}
          {!versionId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('Finance.PlanRollup.PickVersion', { defaultValue: 'Select a plan version.' })}
            </Alert>
          )}
          {versionId && !hasSelection && (
            <Alert severity="info">
              {t('Finance.PlanRollup.PickNodes', { defaultValue: 'Check one or more org nodes in the tree to consolidate.' })}
            </Alert>
          )}

          {rollup && versionId && hasSelection && (
            <>
              {rollup.warnings.map((w, i) => (
                <Alert key={i} severity="warning" sx={{ mb: 2 }}>{w}</Alert>
              ))}

              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                {/* The tiles stay UNSHAPED on purpose (§11): they are the fixed anchor a narrowed tab is
                    read against, and a tile that moved with the filter would leave nothing to compare
                    to. What they must never do is move silently, hence the annotation below. */}
                <SummaryTile
                  label={includeDrafts
                    ? t('Finance.PlanRollup.ResultInclDrafts', { defaultValue: 'Consolidated result (incl. drafts)' })
                    : t('Finance.PlanRollup.ResultSubmitted', { defaultValue: 'Consolidated result (submitted)' })}
                  value={money(rollup.unfilteredSignedResult)}
                >
                  {/* Gross stays as the reconciliation anchor rather than disappearing: it is the number
                      the result is derived from, and the gap between the two is exactly the excluded
                      amount. Same three-figure presentation as the By Account footer, so the tabs agree. */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {rollup.excludedAmount !== 0
                      ? t('Finance.PlanRollup.GrossAndExcludedNote', {
                        defaultValue: 'Gross {{gross}} · {{excluded}} excluded from the result',
                        gross: money(rollup.unfilteredGrandTotal),
                        excluded: money(rollup.excludedAmount),
                      })
                      : t('Finance.PlanRollup.GrossNote', {
                        defaultValue: 'Gross {{gross}} — income and costs added together',
                        gross: money(rollup.unfilteredGrandTotal),
                      })}
                  </Typography>
                  {rollup.signedResultIsApproximate && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                      {t('Finance.PlanRollup.ResultApproximate', {
                        defaultValue: 'Approximate — some accounts have no reporting sign, so it was derived from the account type',
                      })}
                    </Typography>
                  )}
                  {isShaped && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                      {t('Finance.PlanRollup.ShapingActiveTileNote', {
                        defaultValue: 'Unfiltered — the filter and period range apply to the tabs below, not to this figure',
                      })}
                    </Typography>
                  )}
                </SummaryTile>
                <SummaryTile
                  label={t('Finance.PlanRollup.NotYetSubmitted', { defaultValue: 'Not yet submitted' })}
                  value={money(rollup.unfilteredNotYetSubmittedAmount)}
                  color="warning.main"
                >
                  {isShaped && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('Finance.PlanRollup.ShapingActiveTileNote', {
                        defaultValue: 'Unfiltered — the filter and period range apply to the tabs below, not to this figure',
                      })}
                    </Typography>
                  )}
                </SummaryTile>
              </Box>

              <Tabs
                value={safeTab}
                onChange={(_e, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value="period" label={t('Finance.PlanRollup.TabByPeriod', { defaultValue: 'By period & node' })} sx={{ textTransform: 'none' }} />
                {planViewTabs.map((v) => (
                  <Tab key={v.id} value={`pv:${v.id}`} label={v.name} sx={{ textTransform: 'none' }} />
                ))}
                <Tab value="account" label={t('Finance.PlanRollup.TabByAccount', { defaultValue: 'By Account' })} sx={{ textTransform: 'none' }} />
                <Tab value="statement" label={t('Finance.PlanRollup.TabStatement', { defaultValue: 'Report' })} sx={{ textTransform: 'none' }} />
              </Tabs>

              {safeTab === 'period' && (
                <>
                  {/* Permanent on any tenant with internal transfer pricing, so info severity — the
                      amounts are visible in the gross, their absence from the result just must not be
                      silent (PlanRollup-Statement-Templates §3.4, advisory B). */}
                  {rollup.excludedAccountsAdvisory && (
                    <Alert severity="info" sx={{ mb: 2 }}>{rollup.excludedAccountsAdvisory}</Alert>
                  )}
                  <Typography variant="subtitle1" gutterBottom>
                    {t('Finance.PlanRollup.PerPeriod', { defaultValue: 'Per period' })}
                  </Typography>
                  <Paper variant="outlined" sx={{ mb: 1, maxWidth: 520 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('Finance.PlanRollup.ColFiscalPeriod', { defaultValue: 'Fiscal period' })}</TableCell>
                          <TableCell align="right">{t('Finance.PlanRollup.ColResult', { defaultValue: 'Result' })}</TableCell>
                          <TableCell align="right">{t('Finance.PlanRollup.ColGross', { defaultValue: 'Gross' })}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderedPeriods.map((p) => (
                          <TableRow key={p.fiscalPeriodId}>
                            <TableCell>{periodMeta.get(p.fiscalPeriodId)?.name ?? p.fiscalPeriodId}</TableCell>
                            {/* A negative month is a real answer here, not an error — a period whose costs
                                outweigh its income. Coloured so it reads as one at a glance. */}
                            <TableCell align="right" sx={p.signedAmount < 0 ? { color: 'error.main' } : undefined}>
                              {money(p.signedAmount)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{money(p.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {orderedPeriods.length === 0 && (
                          <TableRow><TableCell colSpan={3}>
                            <Typography variant="body2" color="text.secondary">
                              {t('Finance.PlanRollup.EmptyPeriods', { defaultValue: 'No submitted budget under this selection yet.' })}
                            </Typography>
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {filteredLabel(t('Finance.PlanRollup.Total', { defaultValue: 'Total' }), isShaped)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 600, ...(rollup.signedResult < 0 ? { color: 'error.main' } : {}) }}
                          >
                            {money(rollup.signedResult)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>{money(rollup.grandTotal)}</TableCell>
                        </TableRow>
                        <RollupUnfilteredTotalRow
                          isShaped={isShaped}
                          unfilteredTotal={rollup.unfilteredGrandTotal}
                          colSpan={3}
                        />
                      </TableFooter>
                    </Table>
                  </Paper>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, mb: 0.5 }}>
                    <Typography variant="subtitle1">
                      {topMostCheckedIds.length > 1
                        ? t('Finance.PlanRollup.BySelectedNode', { defaultValue: 'By selected node' })
                        : t('Finance.PlanRollup.ByImmediateChild', { defaultValue: 'By immediate child' })}
                    </Typography>
                    {/* Clicking a row re-roots the rollup, which was a one-way trip — this walks it
                        back one drill at a time. Hidden until there is somewhere to go back to. */}
                    {drillTrail.length > 0 && (
                      <Button size="small" startIcon={<ArrowBack />} onClick={drillBack}>
                        {drillBackLabel
                          ? t('Finance.PlanRollup.DrillBackTo', { defaultValue: 'Back to {{node}}', node: drillBackLabel })
                          : t('Finance.PlanRollup.DrillBack', { defaultValue: 'Back' })}
                      </Button>
                    )}
                  </Box>
                  <Paper variant="outlined" sx={{ mb: 1, maxWidth: 720 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('Finance.PlanRollup.ColNode', { defaultValue: 'Node' })}</TableCell>
                          <TableCell>{t('Finance.PlanRollup.ColStatus', { defaultValue: 'Status' })}</TableCell>
                          <TableCell align="right">{t('Finance.PlanRollup.ColResult', { defaultValue: 'Result' })}</TableCell>
                          <TableCell align="right">{t('Finance.PlanRollup.ColGross', { defaultValue: 'Gross' })}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(rollup.breakdown ?? []).map((g) => {
                          const isChild = !g.key.isDirect && !!g.key.childOrgUnitId;
                          const label = g.key.isDirect
                            ? t('Finance.PlanRollup.DirectThisNode', { defaultValue: 'Direct (this node)' })
                            : g.key.childOrgUnitName ?? g.key.childOrgUnitId;
                          return (
                            <TableRow
                              key={g.key.isDirect ? 'direct' : g.key.childOrgUnitId}
                              hover={isChild}
                              sx={isChild ? { cursor: 'pointer' } : undefined}
                              onClick={isChild ? () => drillTo(g.key.childOrgUnitId!) : undefined}
                            >
                              <TableCell>{label}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {g.key.submissionStatuses.map((s) => (
                                    <Chip
                                      key={s}
                                      label={t(`Enum:SubmissionStatus:${s}`, { defaultValue: s })}
                                      size="small"
                                      color={statusChipColor(s)}
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={g.signedTotal < 0 ? { color: 'error.main' } : undefined}>
                                {money(g.signedTotal)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>{money(g.groupTotal)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {(rollup.breakdown ?? []).length === 0 && (
                          <TableRow><TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('Finance.PlanRollup.EmptyNodes', { defaultValue: 'No sub-nodes with budget under this selection.' })}
                            </Typography>
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </>
              )}

              {safeTab.startsWith('pv:') && (
                <PlanRollupPivot
                  versionId={versionId}
                  orgUnitIds={topMostCheckedIds}
                  excludeOrgUnitIds={excludedIds}
                  inclusion={inclusion}
                  planViewId={safeTab.slice(3)}
                  shaping={shaping}
                  unfilteredTotal={rollup.unfilteredGrandTotal}
                />
              )}

              {safeTab === 'account' && (
                <PlanRollupByAccount
                  versionId={versionId}
                  orgUnitIds={topMostCheckedIds}
                  excludeOrgUnitIds={excludedIds}
                  inclusion={inclusion}
                  groupBy={groupBy}
                  onGroupByChange={setGroupBy}
                  shaping={shaping}
                  unfilteredTotal={rollup.unfilteredGrandTotal}
                />
              )}

              {safeTab === 'statement' && (
                <PlanRollupStatement
                  versionId={versionId}
                  orgUnitIds={topMostCheckedIds}
                  excludeOrgUnitIds={excludedIds}
                  inclusion={inclusion}
                  templateId={statementTemplateId || undefined}
                  onTemplateChange={setStatementTemplateId}
                  shaping={shaping}
                  unfilteredTotal={rollup.unfilteredGrandTotal}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default PlanRollupView;
